import React, { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../MainLayout";
import { useSession } from "../../contexts/SessionContext";
import { supabase } from "../../lib/supabase_client";
import '../../styles/page-base.css';

interface Product {
    id: string;
    name: string;
    description: string;
    credits: number;
    price: number;
    country_iso: string;
    currency: string;
    status: "active" | "inactive";
    promotional: boolean;
    promo_code: number;
    promotional_description: string;
    referral_eligible: boolean;
    referral_bonus: number;
    one_time_purchase: boolean;
    created_at: string;
    updated_at: string;
}

interface ProductFormData {
    name: string;
    description: string;
    credits: number;
    price: number;
    status: boolean;
    promotional: boolean;
    promo_code: number;
    promotionalDescription: string;
    referralEligible: boolean;
    referralBonus: number;
    oneTimePurchase: boolean;
    country: string;
}

const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-700";
    //   case "inactive":
    //     return "bg-red-100 text-red-700";
      default:
        return "bg-red-100 text-red-700";
    }
};

const getPromotionalColor = (promotional: boolean, promoCode: number) => {
    if (!promotional || promoCode === 0) {
        return getStatusColor("inactive"); 
    }
    return "bg-purple-100 text-purple-700"; 
};

const getReferralEligibleColor = (eligible: boolean) => {
    return eligible ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700";
};

const getCurrencyByCountry = (countryIso: string): string => {
    switch (countryIso) {
        case 'MY':
            return 'MYR';
        case 'SG':
            return 'SGD';
        case 'GB':
            return 'GBP';
        default:
            return 'MYR'; // Default fallback
    }
};

export default function AdminProductPage() {
    const [searchTerm, setSearchTerm] = useState("");
    const [products, setProducts] = useState<Product[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const [countries, setCountries] = useState<{iso: string, name: string}[]>([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);

    const fetchCountries = useCallback(async () => {
        try {
            setLoadingCountries(true);
            const { data, error } = await supabase
                .from('countries')
                .select('iso, name')
                .order('name', { ascending: true });

            if (error) {
                console.error('Error fetching countries:', error);
                return;
            }

            setCountries(data || []);
        } catch (error) {
            console.error('Error fetching countries:', error);
        } finally {
            setLoadingCountries(false);
        }
    }, []);

    const [productForm, setProductForm] = useState<ProductFormData>({
      name: '',
      description: '',
      credits: 0,
      price: 0,
      status: true,
      promotional: false,
      promo_code: 0,
      promotionalDescription: '',
      referralEligible: false,
      referralBonus: 0,
      oneTimePurchase: false,
      country: 'MY'
    });

    const [savingProduct, setSavingProduct] = useState(false);
    
    const filteredProducts = products.filter(
        (product) => {
            const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                 product.description.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCountry = !selectedCountry || product.country_iso === selectedCountry;
            return matchesSearch && matchesCountry;
        }
    );

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedRows(new Set());
            setSelectAll(false);
        } else {
            const allIds = new Set(currentProducts.map(product => product.id));
            setSelectedRows(allIds);
            setSelectAll(true);
        }
    };
      
    const handleEditProduct = async (product: Product) => {
        setEditingProduct(product);
        
        // Pre-populate the form with existing product data
        setProductForm({
            name: product.name,
            description: product.description,
            credits: product.credits,
            price: product.price,
            status: product.status === 'active',
            promotional: product.promotional,
            promo_code: product.promo_code,
            promotionalDescription: product.promotional_description,
            referralEligible: product.referral_eligible,
            referralBonus: product.referral_bonus,
            oneTimePurchase: product.one_time_purchase,
            country: product.country_iso
        });
        
        setIsEditModalOpen(true);
    };

    const PROMO_CODES = [
        {code : 0, name : 'No Promo'},
        {code : 30, name : 'Free 30 Credits'},
        {code : 50, name : 'Free 50 Credits'},
        {code : 100, name : 'Free 100 Credits'},
    ]
        
    const formatPromotionalText = (product: Product) => {
        if (product.promotional_description) return product.promotional_description;
        if (!product.promotional || product.promo_code === 0) return "No";
        return `Free ${product.promo_code} Credits`;
    };

    const handleRowSelect = (productId: string) => {
        const newSelectedRows = new Set(selectedRows);
        if (newSelectedRows.has(productId)) {
            newSelectedRows.delete(productId);
        } else {
            newSelectedRows.add(productId);
        }
        setSelectedRows(newSelectedRows);
        setSelectAll(newSelectedRows.size === currentProducts.length);
    };

    const handleCreateProduct = async () => {
        setIsCreateModalOpen(true);
    };
      
    const handleProductFormChange = (field: keyof ProductFormData, value: string | number | boolean) => {
        setProductForm(prev => ({
            ...prev,
            [field]: value
        }));
    };
    
    const handleProductSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        try {
            setSavingProduct(true);
            
            const productData = {
                name: productForm.name,
                description: productForm.description,
                credit_amount: productForm.credits,
                price: productForm.price,
                country_iso: productForm.country,
                currency: getCurrencyByCountry(productForm.country),
                is_active: productForm.status,
                is_promotional: productForm.promotional,
                promo_code: productForm.promo_code,
                promotional_description: productForm.promotionalDescription || null,
                referral_eligible: productForm.referralEligible,
                referral_bonus_amount: productForm.referralBonus,
                one_time_purchase: productForm.oneTimePurchase,
                created_at: new Date().toISOString()
            };
        
            let data, error;
            
            if (editingProduct) {
                // Update existing product
                const { data: updateData, error: updateError } = await supabase
                    .from('products')
                    .update(productData)
                    .eq('id', editingProduct.id)
                    .select();
                data = updateData;
                error = updateError;
            } else {
                // Create new product
                const { data: insertData, error: insertError } = await supabase
                    .from('products')
                    .insert([productData])
                    .select();
                data = insertData;
                error = insertError;
            }
        
            if (error) {
                console.error('Error saving product:', error);
                alert(`Failed to ${editingProduct ? 'update' : 'create'} product. Please try again.`);
                return;
            }
        
            console.log(`Product ${editingProduct ? 'updated' : 'created'} successfully:`, data);
            
            // Close modal and reset form
            setIsCreateModalOpen(false);
            setIsEditModalOpen(false);
            setEditingProduct(null);
            setProductForm({
                name: '',
                description: '',
                credits: 0,
                price: 0,
                status: true,
                promotional: false,
                promo_code: 0,
                promotionalDescription: '',
                referralEligible: false,
                referralBonus: 0,
                oneTimePurchase: false,
                country: 'MY',
            });
            
            fetchProducts();
            alert(`Product ${editingProduct ? 'updated' : 'created'} successfully!`);
            
        } catch (error) {
            console.error('Error saving product:', error);
            alert(`Failed to ${editingProduct ? 'update' : 'create'} product. Please try again.`);
        } finally {
            setSavingProduct(false);
        }
    };

    const fetchProducts = useCallback(async () => {
        try {
            setLoadingProducts(true);
            const { data, error } = await supabase
                .from('products')
                .select('id, name, description, credit_amount, price, country_iso, currency, is_active, is_promotional, promo_code, promotional_description, referral_eligible, referral_bonus_amount, one_time_purchase, created_at')
                .order('name', { ascending: true });

            if (error) {
                console.error('Error fetching products:', error);
                return;
            }

            const transformedData = data?.map(item => ({
                id: item.id,
                name: item.name,
                description: item.description,
                credits: item.credit_amount,
                price: item.price,
                country_iso: item.country_iso,
                currency: item.currency,
                status: (item.is_active ? 'active' : 'inactive') as "active" | "inactive",
                promotional: item.is_promotional,
                promo_code: item.promo_code || 0,
                promotional_description: item.promotional_description || '',
                referral_eligible: item.referral_eligible,
                referral_bonus: item.referral_bonus_amount,
                one_time_purchase: item.one_time_purchase ?? false,
                created_at: item.created_at,
                updated_at: item.created_at
            })) || [];

            setProducts(transformedData);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoadingProducts(false);
        }
    }, []);  
    
    useEffect(() => {
        fetchProducts();
        fetchCountries();
    }, [fetchProducts, fetchCountries]);

    const totalPages = Math.ceil(filteredProducts.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const currentProducts = filteredProducts.slice(startIndex, endIndex);
    
    return (
        <MainLayout>
          <div className="page-container">
            <div className="max-w-6xl mx-auto space-y-6">
                
              {/* Header */}
              <div className="page-header">
                <div className="page-header-left">
                <div className="text-sm text-gray-500 mb-1">Admin Only</div>
                <h1 className="page-title">Products</h1>
                </div>
                <div className="page-header-right">
                <button 
                    onClick={handleCreateProduct}
                    className="f-btn f-btn-primary"
                    >
                    <span>New Product</span>
                </button>
                </div>
              </div>
                
                <div className="page-card">
                <div className="flex items-center space-x-3 mb-4">
                <div className="relative">
                    <select 
                        value={selectedCountry || ''} 
                        onChange={(e) => setSelectedCountry(e.target.value || null)}
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                        <option value="">All Countries</option>
                        {countries.map(country => (
                            <option key={country.iso} value={country.iso}>
                                {country.name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Search and Filters */}
                <div className="relative flex-1 max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        </div>
                        <input
                        type="text"
                        placeholder="Search by Product name or Description"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full text-sm pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                </div>
            </div>
            
                {/* Table */}
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
                    <table className="page-table">
                        <thead className="page-table-thead">
                        <tr className="page-table-tr">
                        <th className="page-table-th">
                            <input 
                                type="checkbox" 
                                className="rounded border-gray-300" 
                                checked={selectAll}
                                onChange={handleSelectAll}
                            />
                        </th>
                            {/* <th className="px-4 py-3 w-1/12">
                            <input type="checkbox" className="rounded border-gray-300" />
                            </th> */}
                            <th className="page-table-th">Product Name</th>
                            <th className="page-table-th">Description</th>
                            <th className="page-table-th">Credits</th>
                            <th className="page-table-th">Price</th>
                            <th className="page-table-th">Status</th>
                            <th className="px-3 py-2 w-1/6">Promotional</th>
                            <th className="page-table-th">Referral Eligible</th>
                            <th className="page-table-th">Referral Bonus</th>
                            <th className="page-table-th">One-Time</th>
                            <th className="page-table-th">Actions</th>
                        </tr>
                        </thead>
        
                        <tbody className="page-table-tbody">
                            {loadingProducts ? (
                                <tr className="page-table-tr">
                                    <td colSpan={11} className="text-center py-8 text-gray-500">
                                        Loading...
                                    </td>
                                </tr>
                            ) : currentProducts.length === 0 ? (
                                <tr className="page-table-tr">
                                    <td colSpan={11} className="text-center py-8 text-gray-500">
                                        No products found.
                                    </td>
                                </tr>
                            ) : (
                                currentProducts.map((product, idx) => (
                                    <tr className="page-table-tr"
                                        key={product.id}
                                    >
                                        <td className="page-table-td">
                                            <input 
                                                type="checkbox" 
                                                className="rounded border-gray-300" 
                                                checked={selectedRows.has(product.id)}
                                                onChange={() => handleRowSelect(product.id)}
                                            />
                                        </td>
                                        <td className="page-table-td">
                                            {product.name}
                                        </td>
                                        <td className="page-table-td">{product.description}</td>
                                        <td className="page-table-td text-center">{product.credits}</td>
                                        <td className="page-table-td text-center">
                                            <div className="flex flex-col">
                                                <span>{product.currency}</span>
                                                <span>{product.price}</span>
                                            </div>
                                        </td>
                                        <td className="page-table-td">
                                            <span
                                                className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                                    product.status
                                                )}`}
                                            >
                                                {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
                                            </span>
                                        </td>
                                        <td className="page-table-td">
                                            <span
                                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                    product.promotional_description || (product.promotional && product.promo_code > 0)
                                                        ? 'bg-purple-100 text-purple-700'
                                                        : 'bg-red-100 text-red-700'
                                                }`}
                                            >
                                                {formatPromotionalText(product)}
                                            </span>
                                        </td>
                                        <td className="page-table-td">
                                            <span
                                                className={`px-3 py-1 rounded-full text-xs font-medium ${getReferralEligibleColor(
                                                    product.referral_eligible
                                                )}`}
                                            >
                                                {product.referral_eligible ? "Yes" : "No"}
                                            </span>
                                        </td>
                                        <td className="page-table-td">
                                            {product.referral_bonus > 0 ? `${product.referral_bonus} credits` : "-"}
                                        </td>
                                        <td className="page-table-td">
                                            <span
                                                className={`px-3 py-1 rounded-full text-xs font-medium ${product.one_time_purchase ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}
                                            >
                                                {product.one_time_purchase ? "Yes" : "No"}
                                            </span>
                                        </td>
                                        <td className="page-table-td">
                                            <div className="flex justify-center space-x-2">
                                                <button 
                                                    onClick={() => handleEditProduct(product)}
                                                    className="px-3 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-200 transition"
                                                >
                                                    Edit
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    </div>
                    {/* Pagination */}
                    {!loadingProducts && filteredProducts.length > 0 && (
                        <div className="flex items-center justify-between text-sm text-gray-600 mt-6">
                            <div className="flex items-center space-x-2">
                                <span>Rows per page:</span>
                                <select 
                                    value={rowsPerPage} 
                                    onChange={(e) => setRowsPerPage(Number(e.target.value))}
                                    className="border border-gray-300 rounded px-2 py-1"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                </select>
                            </div>
                            <span>
                                {startIndex + 1}-{Math.min(endIndex, filteredProducts.length)} of {filteredProducts.length}
                            </span>
                            <div className="flex space-x-1">
                                <button
                                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
                                >
                                    &lt;
                                </button>
                                <button
                                    className="f-btn f-btn-primary f-btn-xs"
                                >
                                    {currentPage}
                                </button>
                                <button
                                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
                                >
                                    &gt;
                                </button>
                            </div>
                        </div>
                    )}
                </div>
        </div>
        </div>

        {/* Create Product Modal */}
        {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[95vh] overflow-y-auto relative z-[9999]">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
                <div>
                <h2 className="text-2xl font-bold text-gray-900">Create</h2>
                <p className="text-sm text-gray-600 mt-1">Create a new product</p>
                </div>
                <button
                onClick={() => setIsCreateModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                </button>
            </div>

            {/* Form */}
            <form onSubmit={handleProductSubmit} className="p-6">
                <div className="space-y-6">
                {/* Products Section */}
                <div>
                    {/* <h3 className="text-lg font-semibold text-blue-600 mb-4">Products</h3> */}
                    <div className="space-y-4">
                    {/* Country Field */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Country
                        </label>
                        <select
                            value={productForm.country}
                            onChange={(e) => handleProductFormChange('country', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                        >
                            <option value="">Select Country</option>
                            {countries.map(country => (
                                <option key={country.iso} value={country.iso}>
                                    {country.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Product Name Field */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product Name
                        </label>
                        <input
                        type="text"
                        value={productForm.name}
                        onChange={(e) => handleProductFormChange('name', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter product name"
                        required
                        />
                    </div>

                    {/* Description Field */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                        </label>
                        <textarea
                        value={productForm.description}
                        onChange={(e) => handleProductFormChange('description', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter product description"
                        rows={3}
                        required
                        />
                    </div>

                    {/* Credit Amount Field */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                        Credit Amount
                        </label>
                        <input
                        type="number"
                        value={productForm.credits}
                        onChange={(e) => handleProductFormChange('credits', Number(e.target.value))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter credit amount"
                        required
                        min="0"
                        />
                    </div>

                    {/* Price Field */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                        Price
                        </label>
                        <input
                        type="number"
                        step="0.01"
                        value={productForm.price}
                        onChange={(e) => handleProductFormChange('price', Number(e.target.value))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter price"
                        required
                        min="0"
                        />
                    </div>

                    {/* Status Checkbox */}
                    <div className="flex items-center">
                        <input
                        type="checkbox"
                        id="status"
                        checked={productForm.status}
                        onChange={(e) => handleProductFormChange('status', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="status" className="ml-2 block text-sm text-gray-700">
                        Active
                        </label>
                    </div>

                    {/* Promotional Checkbox */}
                    <div className="flex items-center">
                        <input
                        type="checkbox"
                        id="promotional"
                        checked={productForm.promotional}
                        onChange={(e) => handleProductFormChange('promotional', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="promotional" className="ml-2 block text-sm text-gray-700">
                        Mark as promotional
                        </label>
                    </div>

                    {/* Promo Code Field */}
                    {productForm.promotional && (
                        <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Promo Code
                        </label>
                        <select
                            value={productForm.promo_code}
                            onChange={(e) => handleProductFormChange('promo_code', Number(e.target.value))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                        >
                            <option value="0">Select Promo Code</option>
                            {PROMO_CODES.map(promo => (
                                <option key={promo.code} value={promo.code}>
                                    {promo.name}
                                </option>
                            ))}
                        </select>
                        </div>
                    )}

                    {/* Referral Eligible Checkbox */}
                    <div className="flex items-center">
                        <input
                        type="checkbox"
                        id="referralEligible"
                        checked={productForm.referralEligible}
                        onChange={(e) => handleProductFormChange('referralEligible', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="referralEligible" className="ml-2 block text-sm text-gray-700">
                        Enable referral rewards for this product
                        </label>
                    </div>

                    {/* Referral Bonus Amount Field */}
                    {productForm.referralEligible && (
                        <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Referral Bonus Amount
                        </label>
                        <input
                            type="number"
                            value={productForm.referralBonus}
                            onChange={(e) => handleProductFormChange('referralBonus', Number(e.target.value))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter referral bonus amount"
                            min="0"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            Number of credits the referrer receives when this product is purchased
                        </p>
                        </div>
                    )}

                    {/* One-Time Purchase Checkbox */}
                    <div className="flex items-center">
                        <input
                        type="checkbox"
                        id="oneTimePurchase"
                        checked={productForm.oneTimePurchase}
                        onChange={(e) => handleProductFormChange('oneTimePurchase', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="oneTimePurchase" className="ml-2 block text-sm text-gray-700">
                        One-time purchase only (each client can only buy once)
                        </label>
                    </div>

                    {/* Promotional Description (shown for one-time purchase or promotional) */}
                    {(productForm.oneTimePurchase || productForm.promotional) && (
                        <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Promotional Description
                        </label>
                        <input
                            type="text"
                            value={productForm.promotionalDescription}
                            onChange={(e) => handleProductFormChange('promotionalDescription', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g. Exclusive 30% OFF, Launch Special, FREE 50 Credits"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            Displayed on the offer banner in the purchase dialog
                        </p>
                        </div>
                    )}
                    </div>
                </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t">
                <button
                    type="button"
                    onClick={() => setIsCreateModalOpen(false)}
                    className="f-btn f-btn-soft"
                    disabled={savingProduct}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={savingProduct}
                    className="f-btn f-btn-primary"
                >
                    {savingProduct ? 'Saving...' : 'Save Changes'}
                </button>
                </div>
            </form>
            </div>
        </div>
        )}

        {/* Edit Product Modal */}
        {isEditModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[95vh] overflow-y-auto relative z-[9999]">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Edit Product</h2>
                            <p className="text-sm text-gray-600 mt-1">Update product information</p>
                        </div>
                        <button
                            onClick={() => {
                                setIsEditModalOpen(false);
                                setEditingProduct(null);
                            }}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Form - Same as Create Modal */}
                    <form onSubmit={handleProductSubmit} className="p-6">
                        <div className="space-y-6">
                            <div>
                                <div className="space-y-4">
                                    {/* Country Field */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Country
                                        </label>
                                        <select
                                            value={productForm.country}
                                            onChange={(e) => handleProductFormChange('country', e.target.value)}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            required
                                        >
                                            <option value="">Select Country</option>
                                            {countries.map(country => (
                                                <option key={country.iso} value={country.iso}>
                                                    {country.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Product Name Field */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Product Name
                                        </label>
                                        <input
                                            type="text"
                                            value={productForm.name}
                                            onChange={(e) => handleProductFormChange('name', e.target.value)}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Enter product name"
                                            required
                                        />
                                    </div>

                                    {/* Description Field */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Description
                                        </label>
                                        <textarea
                                            value={productForm.description}
                                            onChange={(e) => handleProductFormChange('description', e.target.value)}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Enter product description"
                                            rows={3}
                                            required
                                        />
                                    </div>

                                    {/* Credit Amount Field */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Credit Amount
                                        </label>
                                        <input
                                            type="number"
                                            value={productForm.credits}
                                            onChange={(e) => handleProductFormChange('credits', Number(e.target.value))}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Enter credit amount"
                                            required
                                            min="0"
                                        />
                                    </div>

                                    {/* Price Field */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Price
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={productForm.price}
                                            onChange={(e) => handleProductFormChange('price', Number(e.target.value))}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Enter price"
                                            required
                                            min="0"
                                        />
                                    </div>

                                    {/* Status Checkbox */}
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="edit-status"
                                            checked={productForm.status}
                                            onChange={(e) => handleProductFormChange('status', e.target.checked)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label htmlFor="edit-status" className="ml-2 block text-sm text-gray-700">
                                            Active
                                        </label>
                                    </div>

                                    {/* Promotional Checkbox */}
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="edit-promotional"
                                            checked={productForm.promotional}
                                            onChange={(e) => handleProductFormChange('promotional', e.target.checked)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label htmlFor="edit-promotional" className="ml-2 block text-sm text-gray-700">
                                            Mark as promotional
                                        </label>
                                    </div>

                                    {/* Promo Code Field */}
                                    {productForm.promotional && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Promo Code
                                            </label>
                                            <select
                                                value={productForm.promo_code}
                                                onChange={(e) => handleProductFormChange('promo_code', e.target.value)}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                required
                                            >
                                                <option value="">Select Promo Code</option>
                                                {PROMO_CODES.map(promo => (
                                                    <option key={promo.code} value={promo.code}>
                                                        {promo.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    {/* Referral Eligible Checkbox */}
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="edit-referralEligible"
                                            checked={productForm.referralEligible}
                                            onChange={(e) => handleProductFormChange('referralEligible', e.target.checked)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label htmlFor="edit-referralEligible" className="ml-2 block text-sm text-gray-700">
                                            Enable referral rewards for this product
                                        </label>
                                    </div>

                                    {/* Referral Bonus Amount Field */}
                                    {productForm.referralEligible && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Referral Bonus Amount
                                            </label>
                                            <input
                                                type="number"
                                                value={productForm.referralBonus}
                                                onChange={(e) => handleProductFormChange('referralBonus', Number(e.target.value))}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Enter referral bonus amount"
                                                min="0"
                                            />
                                            <p className="mt-1 text-xs text-gray-500">
                                                Number of credits the referrer receives when this product is purchased
                                            </p>
                                        </div>
                                    )}

                                    {/* One-Time Purchase Checkbox */}
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="edit-oneTimePurchase"
                                            checked={productForm.oneTimePurchase}
                                            onChange={(e) => handleProductFormChange('oneTimePurchase', e.target.checked)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label htmlFor="edit-oneTimePurchase" className="ml-2 block text-sm text-gray-700">
                                            One-time purchase only (each client can only buy once)
                                        </label>
                                    </div>

                                    {/* Promotional Description (shown for one-time purchase or promotional) */}
                                    {(productForm.oneTimePurchase || productForm.promotional) && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Promotional Description
                                            </label>
                                            <input
                                                type="text"
                                                value={productForm.promotionalDescription}
                                                onChange={(e) => handleProductFormChange('promotionalDescription', e.target.value)}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="e.g. Exclusive 30% OFF, Launch Special, FREE 50 Credits"
                                            />
                                            <p className="mt-1 text-xs text-gray-500">
                                                Displayed on the offer banner in the purchase dialog
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t">
                            <button
                                type="button"
                                onClick={() => {
                                    setIsEditModalOpen(false);
                                    setEditingProduct(null);
                                }}
                                className="f-btn f-btn-soft"
                                disabled={savingProduct}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={savingProduct}
                                className="f-btn f-btn-primary"
                            >
                                {savingProduct ? 'Saving...' : 'Update Product'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        )}
        </MainLayout>
    );
}